import {
    AfterViewInit,
    Component,
    forwardRef,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    QueryList,
    ViewChildren,
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormArray,
    FormBuilder,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator,
} from '@angular/forms';
import { filter, tap } from 'rxjs/operators';

import {
    InteractionEntity,
    InteractionScoringEntity,
    InteractionScoringEntityType,
} from '../../coaching-base/config';
import { FocusDto, NoteCategory } from '../../coaching-base/general';
import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { BringIntoFocusService } from '../bring-into-focus.service';
import { InteractionFormValue } from '../observation-interaction/interaction-form-value.model';
import { InteractionObservationComponent } from '../observation-interaction/interaction.observation.component';
import { createInteraction } from '../observation-interaction/interaction.observation.form';
import { getCurrentScoringConfig } from '../observation-tag-group/observation.focus.summary.component/observation.focus.summary.model';

@Component({
    selector: '[cmpsObservationInteractions]',
    templateUrl: './interactions.observation.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InteractionsObservationComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => InteractionsObservationComponent),
            multi: true,
        },
    ],
})
@TakeUntilDestroy()
export class InteractionsObservationComponent
    implements
        OnInit,
        AfterViewInit,
        OnDestroy,
        ControlValueAccessor,
        Validator,
        TakeUntilDestroy
{
    @Input() storePathPrefix: string;

    @ViewChildren(InteractionObservationComponent)
    items: QueryList<InteractionObservationComponent>;

    @Input() config: InteractionEntity;
    @Input() published: boolean;
    @Input() defaultInteractionTypeId: number;
    @Input() defaultNoteCategory: NoteCategory;
    @Input() focus: FocusDto; // null/undefined = show all
    /** @private */ _form: FormArray;
    private _blackout: boolean;
    private _onChange: (_: InteractionFormValue[]) => void;
    private _onTouched: () => void;
    private _onValidatorChange: () => void;

    /** @private */ get _value(): InteractionFormValue[] {
        return this._form.value;
    }

    constructor(
        private readonly _formBuilder: FormBuilder,
        @Optional() private readonly _focusService: BringIntoFocusService,
    ) {}

    ngOnInit(): void {
        this._form = new FormArray([]);

        this._form.valueChanges
            .pipe(
                filter(() => !this._blackout),
                tap((value1) => {
                    CMPS_APP_DEF.ENV !== 'prod' &&
                        console.log('_onChange interactions', value1);
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    this._onChange && this._onChange(value1);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    ngAfterViewInit(): void {
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const run = () => {
            this.items.forEach((e, i) => {
                (e as any).onRemove(() => this.removeInteraction(i));
            });
        };
        this.items.changes
            .pipe(
                tap(() => {
                    run();
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
        run();
    }

    ngOnDestroy(): void {
        this._form && (this._form as any).clear2();
    }

    registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        isDisabled
            ? this._form.disable({ emitEvent: false })
            : this._form.enable({ emitEvent: false });
    }

    writeValue(obj: InteractionFormValue[]): void {
        this._patchForm(obj);
    }

    registerOnValidatorChange(fn: () => void): void {
        this._onValidatorChange = fn;
    }

    validate(control: AbstractControl): ValidationErrors | null {
        this._form.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        return this._form.errors;
    }

    addInteraction(): void {
        let currentScoringConfigId: number;
        try {
            const scoringEntity = this.config.childEntities.find(
                (e) => e.entityType === InteractionScoringEntityType,
            ) as InteractionScoringEntity;
            currentScoringConfigId =
                getCurrentScoringConfig(scoringEntity).scoringConfigurationId;
        } catch {
            //
        }

        const item = createInteraction(
            this.defaultInteractionTypeId,
            currentScoringConfigId,
        );
        this._form.push(this._formBuilder.control(item));

        if (this._focusService) {
            this._focusService.registerSelector(
                `[data-trackingid='${item.trackingId}']`,
                () => {
                    this.items.last.focus();
                },
            );
        }
    }

    removeInteraction(index: number): void {
        this._form.removeAt(index);
    }

    private _patchForm(value: InteractionFormValue[]): void {
        this._blackout = true;

        (this._form as any).clear2();
        (this._form as any).push2(
            ...(value || []).map((e) => this._formBuilder.control(e)),
        );

        this._blackout = false;
    }
}
